import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TaskAssignee, AssignableUser } from '@/types/tasks';
import { toast } from 'sonner';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';

// Helper function to validate UUID format
const isValidUUID = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

// Helper function to check if it's a temporary ID
const isTemporaryId = (id: string): boolean => {
  return id.startsWith('temp-') || id.includes('temp');
};

// Hook to fetch assignees for a specific task using RPC function
export const useTaskAssignees = (taskId: string | undefined) => {
  return useQuery({
    queryKey: ['task-assignees', taskId],
    queryFn: async (): Promise<TaskAssignee[]> => {
      if (!taskId) return [];
      
      // CRITICAL: Don't make API calls for temporary or invalid task IDs
      if (isTemporaryId(taskId) || !isValidUUID(taskId)) {
        console.debug('TaskAssignees: Skipping API call for invalid/temporary task ID:', taskId);
        return [];
      }
      
      // PERFORMANCE: Check if this task is associated with a project before making assignee API call
      // Standalone tasks (goalId = null) don't have assignees, so skip the expensive RPC call
      try {
        const { data: taskCheck, error: taskCheckError } = await supabase
          .from('planner_tasks')
          .select('goal_id')
          .eq('id', taskId)
          .single();
          
        if (taskCheckError || !taskCheck) {
          console.debug('TaskAssignees: Task not found or access denied:', taskId);
          return [];
        }
        
        // If task has no goal_id, it's a standalone task with no assignees
        if (!taskCheck.goal_id) {
          console.debug('TaskAssignees: Standalone task detected, skipping assignee lookup:', taskId);
          return [];
        }
      } catch (error) {
        console.debug('TaskAssignees: Error checking task project association:', error);
        return [];
      }
      
      try {
        // Use the get_task_assignees RPC function for better performance and RLS compliance
        const { data, error } = await supabase.rpc('get_task_assignees', {
          p_task_id: taskId
        });

      if (error) {
          // Handle access denied errors gracefully - they're expected in some contexts
          if (error.message?.includes('Access denied')) {
            console.debug('TaskAssignees: Access denied for task', taskId, '- this is expected for tasks outside user\'s collaboration scope');
            return [];
          }
          
        console.error('Failed to fetch task assignees:', error);
          throw error;
        }

        // Data already matches TaskAssignee interface from the RPC function
        return data || [];
      } catch (error: any) {
        // Handle network errors or RLS denials gracefully
        if (error?.message?.includes('Access denied') || error?.code === 'P0001') {
          console.debug('TaskAssignees: Access denied or permission error for task', taskId);
          return [];
        }
        throw error;
      }
    },
    enabled: !!taskId && isValidUUID(taskId) && !isTemporaryId(taskId),
    // Add retry and error handling configuration
    retry: (failureCount, error: any) => {
      // Don't retry on access denied errors
      if (error?.message?.includes('Access denied') || error?.code === 'P0001') {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
    // Cache successful results for 5 minutes
    staleTime: 5 * 60 * 1000
  });
};

// Hook to fetch assignable users for a specific task (using team members from workbook)
export const useAssignableUsers = (taskId: string | undefined) => {
  return useQuery({
    queryKey: ['assignable-users', taskId],
    queryFn: async (): Promise<AssignableUser[]> => {
      if (!taskId) return [];
      
      // First, get the workbook ID for this task
      const { data: taskData, error: taskError } = await supabase
        .from('planner_tasks')
        .select(`
          goal_id,
          planner_goals!goal_id (
            planner_goals_workbooks (
              workbook_id
            )
          )
        `)
        .eq('id', taskId)
        .single();

      if (taskError || !taskData) {
        console.error('Failed to fetch task workbook:', taskError);
        throw taskError || new Error('Task not found');
      }

      const workbookId = taskData.planner_goals?.planner_goals_workbooks?.[0]?.workbook_id;
      if (!workbookId) {
        return []; // Task is not associated with a workbook
      }

      // Get team members using the existing workbook_collaboration function
      const { data: teamData, error: teamError } = await supabase.rpc('workbook_collaboration', {
        p_action: 'get_editors',
        p_workbook_id: workbookId
      });

      if (teamError) {
        console.error('Failed to fetch team members:', teamError);
        throw teamError;
      }

      // Get current assignees to mark them as already assigned
      const { data: assigneesData } = await supabase
        .from('planner_tasks_assignees')
        .select('assignee_id')
        .eq('task_id', taskId);

      const assignedUserIds = new Set((assigneesData || []).map((a: any) => a.assignee_id));

      // Transform team data to AssignableUser format
      const assignableUsers: AssignableUser[] = (teamData as any[] || []).map((member: any) => ({
        user_id: member.id,
        first_name: member.first_name || '',
        username: member.username || '',
        profile_picture_url: member.profile_picture_url,
        display_name: member.display_name || member.first_name || member.username || 'Unknown User',
        is_already_assigned: assignedUserIds.has(member.id)
      }));

      return assignableUsers;
    },
    enabled: !!taskId
  });
};

// Hook to add a task assignee
export const useAddTaskAssignee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, assigneeId }: { taskId: string; assigneeId: string }) => {
      const { data, error } = await supabase
        .from('planner_tasks_assignees')
        .insert({
          task_id: taskId,
          assignee_id: assigneeId,
          assigned_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) {
        console.error('Failed to add task assignee:', error);
        throw error;
      }

      return data;
    },
    onSuccess: async (_, { taskId, assigneeId }) => {
      // Invalidate task-specific queries
      queryClient.invalidateQueries({ queryKey: ['task-assignees', taskId] });
      queryClient.invalidateQueries({ queryKey: ['assignable-users', taskId] });
      
      // Invalidate TaskManager cache keys for all users who might be affected
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Invalidate for the current user
        queryClient.invalidateQueries({ queryKey: ['task-manager-tasks', user.id] });
        queryClient.invalidateQueries({ queryKey: ['assigned-tasks-count', user.id] });
        
        // Also invalidate for the assignee if different
        if (assigneeId !== user.id) {
          queryClient.invalidateQueries({ queryKey: ['task-manager-tasks', assigneeId] });
          queryClient.invalidateQueries({ queryKey: ['assigned-tasks-count', assigneeId] });
        }
        
        // CREATE TASK ASSIGNMENT NOTIFICATION
        try {
          // Get task details and assigner profile
          const [taskResult, assignerResult] = await Promise.all([
            supabase
              .from('planner_tasks')
              .select('title, user_id')
              .eq('id', taskId)
              .single(),
            supabase
              .from('public_profile')
              .select('first_name, username')
              .eq('id', user.id)
              .single()
          ]);

          if (taskResult.data && assignerResult.data) {
            const taskTitle = taskResult.data.title;
            const assignerName = assignerResult.data.first_name || assignerResult.data.username || 'Someone';
            
            // Only create notification if assigner is different from assignee
            if (user.id !== assigneeId) {
              await supabase.rpc('create_notification', {
                p_user_id: assigneeId,
                p_title: 'New Task Assignment',
                p_message: `You have been assigned to "${taskTitle}" by ${assignerName}`,
                p_type: 'info',
                p_link: '/task-manager',
                p_action_label: 'View Task',
                p_source_type: 'task_assignment',
                p_source_id: taskId
              });
              
              console.log('[Notification] Task assignment notification created for user:', assigneeId);
            }
          }
        } catch (notificationError) {
          console.error('[Notification] Failed to create task assignment notification:', notificationError);
          // Don't fail the main operation for notification errors
        }
      }
      
      // Trigger quest completion for team member assignment (Step 8)
      QuestTriggers.teamMemberAdded();
      
      toast.success('Assignee added successfully');
    },
    onError: (error) => {
      toast.error(`Failed to add assignee: ${error.message}`);
    }
  });
};

// Hook to remove a task assignee
export const useRemoveTaskAssignee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, assigneeId }: { taskId: string; assigneeId: string }) => {
      const { error } = await supabase
        .from('planner_tasks_assignees')
        .delete()
        .eq('task_id', taskId)
        .eq('assignee_id', assigneeId);

      if (error) {
        console.error('Failed to remove task assignee:', error);
        throw error;
      }

      return { success: true };
    },
    onSuccess: async (_, { taskId, assigneeId }) => {
      // Invalidate task-specific queries
      queryClient.invalidateQueries({ queryKey: ['task-assignees', taskId] });
      queryClient.invalidateQueries({ queryKey: ['assignable-users', taskId] });
      
      // Invalidate TaskManager cache keys for all users who might be affected
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Invalidate for the current user
        queryClient.invalidateQueries({ queryKey: ['task-manager-tasks', user.id] });
        queryClient.invalidateQueries({ queryKey: ['assigned-tasks-count', user.id] });
        
        // Also invalidate for the assignee if different
        if (assigneeId !== user.id) {
          queryClient.invalidateQueries({ queryKey: ['task-manager-tasks', assigneeId] });
          queryClient.invalidateQueries({ queryKey: ['assigned-tasks-count', assigneeId] });
        }
      }
      
      toast.success('Assignee removed successfully');
    },
    onError: (error) => {
      toast.error(`Failed to remove assignee: ${error.message}`);
    }
  });
};

// Hook to get team members for a given workbook (for new task creation)
export const useWorkbookTeamMembers = (workbookId: string | undefined) => {
  return useQuery({
    queryKey: ['workbook-team-members', workbookId],
    queryFn: async (): Promise<AssignableUser[]> => {
      if (!workbookId) return [];
      
      const { data, error } = await supabase.rpc('workbook_collaboration', {
        p_action: 'get_editors',
        p_workbook_id: workbookId
      });

      if (error) {
        console.error('Failed to fetch workbook team members:', error);
        throw error;
      }

      // Transform the editor data to match AssignableUser format
      const teamMembers: AssignableUser[] = (data as any[] || []).map((editor: any) => ({
        user_id: editor.id,
        first_name: editor.first_name || '',
        username: editor.username || '',
        profile_picture_url: editor.profile_picture_url,
        display_name: editor.display_name || editor.first_name || editor.username || 'Unknown User',
        is_already_assigned: false // For new tasks, no one is assigned yet
      }));

      return teamMembers;
    },
    enabled: !!workbookId
  });
}; 